import type { PersistedDefaultTabProps, TabProps } from '@talos/kyoko';
import { useMemo } from 'react';
import { getTabsStateFromPanelConfig, type PanelConfigWithTabs } from './navRoutes';

export interface ResolvedTabResult<T extends TabProps> {
  /** The full tab config object (persisted or default), or undefined if no match. */
  tab: (T | PersistedDefaultTabProps<T>) | undefined;
  /** Whether the resolved tab came from persisted panelConfig (vs. defaults). */
  isPersisted: boolean;
}

/**
 * Resolves the active tab's full config (filter, columns, label, etc.) for a
 * mobile blotter, using the same precedence rules as `usePanelTabNavItems`:
 * persisted `panelConfig.tabs.items` wins when non-empty, otherwise falls back
 * to the supplied defaults.
 */
export function useResolvedTab<T extends TabProps>({
  panelConfig,
  defaultTabs,
  tabId,
  isTabIncluded,
  mapTab,
}: {
  panelConfig: PanelConfigWithTabs<T> | undefined;
  defaultTabs: PersistedDefaultTabProps<T>[];
  tabId: string | undefined;
  isTabIncluded?: (tab: T | PersistedDefaultTabProps<T>) => boolean;
  mapTab?: (tab: T | PersistedDefaultTabProps<T>) => T | PersistedDefaultTabProps<T>;
}): ResolvedTabResult<T> {
  return useMemo(() => {
    if (tabId == null) {
      return { tab: undefined, isPersisted: false };
    }
    const tabsState = getTabsStateFromPanelConfig(panelConfig);
    const persisted = (tabsState?.items ?? []).filter(tab => isTabIncluded?.(tab) ?? true);
    if (persisted.length > 0) {
      const match = persisted.find(t => t.id === tabId);
      if (match) {
        return { tab: mapTab ? mapTab(match) : match, isPersisted: true };
      }
    }
    const fallback = defaultTabs.find(t => t.id === tabId && (isTabIncluded?.(t) ?? true));
    return { tab: fallback != null && mapTab ? mapTab(fallback) : fallback, isPersisted: false };
  }, [panelConfig, defaultTabs, tabId, isTabIncluded, mapTab]);
}
